import React, { useEffect, useRef, useState } from 'react'
import { createSpeechRecognitionPonyfill, SpeechGrammarList } from '@davi-ai/web-speech-cognitive-services-davi'
import type { PonyfillOptions, SpeechRecognitionPonyfillType, SpeechRecognitionProps, SpeechRecognitionResultList, SpeechRecognitionResultListItem } from '@davi-ai/web-speech-cognitive-services-davi'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../store'
import { Mode, PermissionStatus, RecognitionState, Routes } from '../../models/enums'
import getMicrophonePermission from '../../utils/getMicrophonePermission'

const timeoutDuration = 1000

const RecognitionManager = (): JSX.Element => {
  const dispatch = useDispatch()
  const locale = useSelector((state: RootState) => state.localeReducer.locale)
  const mode = useSelector((state: RootState) => state.speechReducer.mode)
  const appAvailable = useSelector((state: RootState) => state.retorikReducer.appAvailable)
  const loaderClosed = useSelector((state: RootState) => state.retorikReducer.loaderClosed)
  const agentData = useSelector((state: RootState) => state.retorikReducer.agentData)
  const delay = useSelector((state: RootState) => state.retorikReducer.submitDelayBeforeSendingMessage)
  const speechRecognitionOptions = useSelector((state: RootState) => state.retorikReducer.configuration.speechRecognitionOptions)
  const ponyfillCredentials = useSelector((state: RootState) => state.speechReducer.ponyfillCredentials)
  const useContinuousRecognition = useSelector((state: RootState) => state.speechReducer.useContinuousRecognition)
  const activeRecognitionState = useSelector((state: RootState) => state.speechReducer.activeRecognitionState)
  const lastRecognitionInterim = useSelector((state: RootState) => state.speechReducer.lastRecognitionInterim)
  const speechRecognitionDynamicGrammars = useSelector((state: RootState) => state.speechReducer.speechRecognitionDynamicGrammars)
  const route = useSelector((state: RootState) => state.viewReducer.route)

  const [ponyfill, setPonyfill] = useState<SpeechRecognitionPonyfillType | null>(null)
  const [recognitionAllowed, setRecognitionAllowed] = useState<boolean>(false)
  const [ponyfillReady, setPonyfillReady] = useState<boolean>(false)

  const activeModeRef = useRef<RecognitionState>()
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const localeRef = useRef<string | undefined>(undefined)

  const getCommonAndLocalizedWakeWords = (currentLocale: string, agentName?: string): Array<string> => {
    let commonAndlocalizedWakeWords: Array<string> = []
    if (speechRecognitionOptions?.wakeWords) {
      if (speechRecognitionOptions.wakeWords.common) {
        commonAndlocalizedWakeWords = [...speechRecognitionOptions.wakeWords.common]
      }

      if (speechRecognitionOptions.wakeWords.localized) {
        const localizedWakeWords = speechRecognitionOptions.wakeWords.localized[currentLocale] || []
        localizedWakeWords.length && (commonAndlocalizedWakeWords = [...commonAndlocalizedWakeWords, ...localizedWakeWords])
      }
    }

    // Add agent's name to wake words
    agentName && (commonAndlocalizedWakeWords = [...commonAndlocalizedWakeWords, agentName])

    return commonAndlocalizedWakeWords
  }

  const getCommonAndLocalizedGrammars = (currentLocale: string): Array<string> => {
    let commonAndlocalizedGrammars: Array<string> = []
    if (speechRecognitionOptions?.grammars) {
      if (speechRecognitionOptions.grammars.common) {
        commonAndlocalizedGrammars = [...speechRecognitionOptions.grammars.common]
      }

      if (speechRecognitionOptions.grammars.localized) {
        const localizedGrammars = speechRecognitionOptions.grammars.localized[currentLocale] || []
        localizedGrammars.length && (commonAndlocalizedGrammars = [...commonAndlocalizedGrammars, ...localizedGrammars])
      }
    }

    return commonAndlocalizedGrammars
  }

  const askPermission = (): void => {
    if (recognitionAllowed) {
      ponyfill?.speechRecognition.start()
    } else {
      getMicrophonePermission()
        .then((permission) => {
          setRecognitionAllowed(permission === PermissionStatus.allowed)
        })
        .catch(console.log)
    }
  }

  useEffect(() => {
    // Clear data on component unmount
    return () => {
      timerRef?.current && clearTimeout(timerRef.current)
      ponyfill?.speechRecognition?.abort?.()
    }
  }, [])

  useEffect(() => {
    recognitionAllowed && useContinuousRecognition && ponyfill?.speechRecognition.start()
  }, [recognitionAllowed])

  useEffect(() => {
    if (ponyfillCredentials && locale && agentData) {
      let currentPonyfill = ponyfill
      if (locale !== localeRef.current) {
        localeRef.current = locale
        // We create the ponyfill if it isn't created yet. We recreate a new one if we are using custom endpoints for speech recognition.
        if (!ponyfill || ponyfillCredentials?.speechRecognitionHostnamesRecord) {
          // Check if we need to use a custom endpoint from speechRecognitionHostnamesRecord
          const speechRecognitionHostname = ponyfillCredentials?.speechRecognitionHostnamesRecord
            ? ponyfillCredentials.speechRecognitionHostnamesRecord[locale] || Object.values(ponyfillCredentials.speechRecognitionHostnamesRecord)[0]
            : undefined

          const options: SpeechRecognitionProps = {
            autoStart: false,
            continuous: useContinuousRecognition,
            interimResults: true,
            grammarsList: getCommonAndLocalizedGrammars(locale),
            lang: locale,
            passive: useContinuousRecognition,
            wakeWords: getCommonAndLocalizedWakeWords(locale),
            debug: false,
            timerBeforeSpeechEnd: speechRecognitionOptions?.timerBeforeSpeechEnd
          }

          const processedCredentials: PonyfillOptions = {
            credentials: speechRecognitionHostname
              ? {
                  region: undefined,
                  subscriptionKey: ponyfillCredentials.subscriptionKey || 'mandatory_subscription_key',
                  speechRecognitionHostname: speechRecognitionHostname,
                  speechSynthesisHostname: 'mandatory_synthesis_host_name'
                }
              : {
                  region: ponyfillCredentials.region,
                  subscriptionKey: ponyfillCredentials.subscriptionKey,
                  authorizationToken: ponyfillCredentials.authorizationToken
                }
          }

          // Abort current recognition before setting the new recognition ponyfill if there is a need to
          currentPonyfill?.speechRecognition?.abort?.()
          currentPonyfill = createSpeechRecognitionPonyfill(processedCredentials, options)
        } else if (currentPonyfill) {
          // Set wakeworkds
          currentPonyfill.speechRecognition.wakeWords = getCommonAndLocalizedWakeWords(locale, agentData?.name)
          // Set language
          activeRecognitionState === RecognitionState.Listening ? currentPonyfill?.speechRecognition.changeLanguage(locale) : (currentPonyfill.speechRecognition.lang = locale)
        }
      }

      if (currentPonyfill) {
        // Set grammar list
        const grammarList = new SpeechGrammarList()
        grammarList.phrases = getCommonAndLocalizedGrammars(locale)
        if (speechRecognitionDynamicGrammars.length) {
          speechRecognitionDynamicGrammars.forEach((grammar) => {
            !grammarList.phrases.includes(grammar) && grammarList.phrases.push(grammar)
          })
        }

        if (agentData?.name) {
          // Add raw name
          !grammarList.phrases.includes(agentData.name) && grammarList.phrases.push(agentData.name)
          // Add lower-cased name
          !grammarList.phrases.includes(agentData.name.toLocaleLowerCase(locale)) && grammarList.phrases.push(agentData.name.toLocaleLowerCase(locale))
        }
        currentPonyfill.speechRecognition.grammars = grammarList

        setPonyfill(currentPonyfill)
      }
    }
  }, [ponyfillCredentials, locale, speechRecognitionDynamicGrammars, agentData])

  /**
   * Abort recognition if we are not on the 'home' page
   */
  useEffect(() => {
    route !== Routes.Home && dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closed))
  }, [route])

  useEffect(() => {
    if (ponyfill && appAvailable && loaderClosed) {
      if (ponyfill.speechRecognition) {
        ponyfill.speechRecognition.onstart = () => {
          dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Listening))
        }

        // onend callback is called only whithout using passive speech recognition
        ponyfill.speechRecognition.onend = () => {
          activeModeRef.current !== RecognitionState.Closed && dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closing))
        }

        ponyfill.speechRecognition.onabort = () => {
          dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closed))
        }

        ponyfill.speechRecognition.onwakeup = () => {
          if (activeModeRef.current === RecognitionState.Closed) {
            console.log('Retorik Framework > wake word found, trying to open microphone')
            activeModeRef.current === RecognitionState.Closed && dispatch(storeActions.speech.toggleMicrophone(false))
          } else {
            console.log('Retorik Framework > wake word found, but microphone not ready')
          }
        }

        ponyfill.speechRecognition.onresult = (result: SpeechRecognitionResultList | Array<SpeechRecognitionResultListItem>) => {
          const lastMessage: string | null = result.length ? result[result.length - 1].transcript : null
          // Only get result in Active mode
          if (lastMessage && activeModeRef.current === RecognitionState.Listening) {
            dispatch(storeActions.speech.setLastRecognitionInterim(lastMessage))
          }
        }

        setPonyfillReady(true)
      }
    }
  }, [ponyfill, appAvailable, loaderClosed])

  useEffect(() => {
    if (ponyfillReady && mode === Mode.vocal) {
      // Start passive recognition if not done yet
      useContinuousRecognition && !ponyfill?.speechRecognition.started && askPermission()
    }
  }, [mode, ponyfillReady])

  const togglePassiveToActive = (): void => {
    if (ponyfill?.speechRecognition.started) {
      ponyfill.speechRecognition.toggleContinuousPassiveToActive()
    } else {
      if (ponyfill) {
        ponyfill.speechRecognition.passive = false
        ponyfill.speechRecognition.continuous = false
        ponyfill.speechRecognition.start()
      }
    }
  }

  useEffect(() => {
    activeModeRef.current = activeRecognitionState

    if (ponyfill?.speechRecognition && appAvailable && loaderClosed) {
      timerRef?.current && clearTimeout(timerRef.current)
      switch (activeRecognitionState) {
        // Do nothing if we are in a transition state
        case RecognitionState.Initializing: {
          useContinuousRecognition ? togglePassiveToActive() : ponyfill.speechRecognition.start()
          break
        }
        case RecognitionState.Listening:
          break
        case RecognitionState.Closing: {
          if (lastRecognitionInterim) {
            if (!delay) {
              timerRef.current = setTimeout(() => {
                dispatch(storeActions.directline.sendMessage({ text: lastRecognitionInterim }))

                dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closed))
              }, timeoutDuration)
            }
          } else {
            dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closed))
          }
          break
        }
        case RecognitionState.ForceClosing: {
          dispatch(storeActions.speech.setLastRecognitionInterim(''))

          // Let's abort the current recognition
          ponyfill.speechRecognition.started ? ponyfill.speechRecognition.abort?.() : dispatch(storeActions.speech.setActiveRecognitionState(RecognitionState.Closed))
          break
        }
        case RecognitionState.Closed: {
          if (useContinuousRecognition && mode === Mode.vocal) {
            timerRef.current = setTimeout(() => {
              ponyfill.speechRecognition.toggleContinuousActiveToPassive()
            }, timeoutDuration)
          }
          dispatch(storeActions.speech.setLastRecognitionInterim(''))
          break
        }
      }
    }
  }, [activeRecognitionState])

  return <React.Fragment />
}

export default RecognitionManager
